// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Dashboard
 * @package @geenius/analytics
 * @description Shared dashboard widget contracts and aggregation helpers used
 * by the React, SolidJS, and Storybook surfaces. This module normalizes raw
 * analytics data into the consistent dashboard model exposed by the package.
 */

import type {
  AnalyticsDashboardData,
  AnalyticsEvent,
  ChartDataPoint,
  CohortAnalysis,
  EventType,
  FunnelResult,
  MetricSummary,
  RealtimeStats,
  SessionReplaySnapshot,
  TopPageSummary,
} from "./types";

/** Supported dashboard widget kinds rendered by the shared analytics dashboards. */
export type DashboardWidgetKind =
  | "metric"
  | "chart"
  | "top-pages"
  | "device-breakdown"
  | "events"
  | "funnel"
  | "realtime"
  | "cohort"
  | "replay";

/**
 * Shared metadata present on every dashboard widget model.
 *
 * @property id Stable widget identifier used for rendering and diffing.
 * @property kind Widget kind that determines the renderer and data payload.
 * @property title Human-readable widget title shown in dashboards and Storybook fixtures.
 * @property description Optional supporting description for the widget card.
 * @property span Optional grid span used by composed dashboard layouts.
 */
export interface DashboardWidgetBase {
  id: string;
  kind: DashboardWidgetKind;
  title: string;
  description?: string;
  span?: 1 | 2 | 3;
}

/**
 * Dashboard widget that renders a single summary metric.
 *
 * @property metric Metric payload shown by metric-focused dashboard cards.
 */
export interface MetricDashboardWidget extends DashboardWidgetBase {
  kind: "metric";
  metric: MetricSummary;
}

/**
 * Dashboard widget that renders a chart-backed series.
 *
 * @property chartType Optional visualization type used by the chart renderer.
 * @property data Ordered chart points consumed by the visual component.
 */
export interface ChartDashboardWidget extends DashboardWidgetBase {
  kind: "chart";
  chartType?: "bar" | "line";
  data: ChartDataPoint[];
}

/**
 * Dashboard widget that lists the top-performing pages.
 *
 * @property pages Ranked page summary entries derived from analytics traffic.
 */
export interface TopPagesDashboardWidget extends DashboardWidgetBase {
  kind: "top-pages";
  pages: AnalyticsDashboardData["topPages"];
}

/**
 * Dashboard widget that summarizes device distribution.
 *
 * @property breakdown Device-count aggregate broken out by desktop, tablet, and mobile.
 */
export interface DeviceBreakdownDashboardWidget extends DashboardWidgetBase {
  kind: "device-breakdown";
  breakdown: AnalyticsDashboardData["deviceBreakdown"];
}

/**
 * Dashboard widget that renders a recent-event stream.
 *
 * @property events Event rows shown in the widget.
 * @property maxRows Optional row cap used by compact dashboard layouts.
 */
export interface EventDashboardWidget extends DashboardWidgetBase {
  kind: "events";
  events: AnalyticsEvent[];
  maxRows?: number;
}

/**
 * Dashboard widget that renders funnel conversion output.
 *
 * @property result Funnel analysis payload rendered by the widget.
 */
export interface FunnelDashboardWidget extends DashboardWidgetBase {
  kind: "funnel";
  result: FunnelResult;
}

/**
 * Dashboard widget that renders realtime activity output.
 *
 * @property stats Latest realtime analytics snapshot.
 * @property isLoading Optional loading state for live polling UIs.
 */
export interface RealtimeDashboardWidget extends DashboardWidgetBase {
  kind: "realtime";
  stats: RealtimeStats | null;
  isLoading?: boolean;
}

/**
 * Dashboard widget that renders cohort-retention output.
 *
 * @property analysis Cohort analysis payload rendered by the widget.
 */
export interface CohortDashboardWidget extends DashboardWidgetBase {
  kind: "cohort";
  analysis: CohortAnalysis;
}

/**
 * Dashboard widget that previews a session-replay snapshot.
 *
 * @property replay Session replay material used for the widget preview.
 */
export interface ReplayDashboardWidget extends DashboardWidgetBase {
  kind: "replay";
  replay: SessionReplaySnapshot;
}

/** Union of all dashboard widgets supported by the shared dashboard model. */
export type DashboardWidget =
  | MetricDashboardWidget
  | ChartDashboardWidget
  | TopPagesDashboardWidget
  | DeviceBreakdownDashboardWidget
  | EventDashboardWidget
  | FunnelDashboardWidget
  | RealtimeDashboardWidget
  | CohortDashboardWidget
  | ReplayDashboardWidget;

/**
 * Shared dashboard layout consumed by the React, SolidJS, and Storybook surfaces.
 *
 * @property title Optional dashboard heading.
 * @property description Optional supporting copy describing the dashboard purpose.
 * @property widgets Ordered widget collection rendered by the dashboard builders.
 */
export interface DashboardLayout {
  title?: string;
  description?: string;
  widgets: DashboardWidget[];
}

function normalizeEventType(type: string): EventType {
  switch (type) {
    case "page_view":
    case "click":
    case "form_submit":
    case "custom":
      return type;
    default:
      return "custom";
  }
}

/** Fluent builder for composing analytics dashboard layouts in user code and tests. */
export class DashboardBuilder {
  private readonly widgets: DashboardWidget[] = [];
  private title?: string;
  private description?: string;

  /**
   * Sets the dashboard title and optional description.
   *
   * @param title Primary heading for the dashboard layout.
   * @param description Optional supporting description shown with the heading.
   * @returns The same builder instance for fluent chaining.
   */
  withTitle(title: string, description?: string): this {
    this.title = title;
    this.description = description;
    return this;
  }

  /**
   * Adds a metric widget to the layout.
   *
   * @param widget Metric widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  metric(widget: MetricDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds a chart widget to the layout.
   *
   * @param widget Chart widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  chart(widget: ChartDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds a top-pages widget to the layout.
   *
   * @param widget Top-pages widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  topPages(widget: TopPagesDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds a device-breakdown widget to the layout.
   *
   * @param widget Device-breakdown widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  deviceBreakdown(widget: DeviceBreakdownDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds an event-stream widget to the layout.
   *
   * @param widget Event widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  events(widget: EventDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds a funnel widget to the layout.
   *
   * @param widget Funnel widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  funnel(widget: FunnelDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds a realtime widget to the layout.
   *
   * @param widget Realtime widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  realtime(widget: RealtimeDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds a cohort-analysis widget to the layout.
   *
   * @param widget Cohort widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  cohort(widget: CohortDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Adds a replay widget to the layout.
   *
   * @param widget Replay widget definition to append.
   * @returns The same builder instance for fluent chaining.
   */
  replay(widget: ReplayDashboardWidget): this {
    this.widgets.push(widget);
    return this;
  }

  /**
   * Finalizes the composed dashboard layout.
   *
   * @returns Dashboard layout containing a defensive copy of the accumulated widgets.
   */
  build(): DashboardLayout {
    return {
      title: this.title,
      description: this.description,
      widgets: [...this.widgets],
    };
  }
}

/**
 * Creates a fluent dashboard builder for composing layout definitions.
 *
 * @returns New dashboard builder instance with no widgets configured yet.
 */
export function createDashboardBuilder(): DashboardBuilder {
  return new DashboardBuilder();
}

/**
 * Creates a dashboard layout from a widget array and optional metadata.
 *
 * @param widgets Ordered widgets that should appear in the dashboard layout.
 * @param meta Optional dashboard title and description metadata.
 * @returns Shared dashboard layout ready for framework-specific rendering.
 */
export function createDashboardLayout(
  widgets: DashboardWidget[],
  meta: Omit<DashboardLayout, "widgets"> = {},
): DashboardLayout {
  return {
    ...meta,
    widgets: [...widgets],
  };
}

/**
 * Convex analytics-event row shape consumed when normalizing packaged query results.
 *
 * @property event Event name captured by the analytics provider.
 * @property type Event type label used to derive the shared event taxonomy.
 * @property properties Optional JSON-safe analytics properties.
 * @property url URL associated with the event.
 * @property referrer Optional referrer captured alongside the event.
 * @property userId Optional user identity associated with the event.
 * @property device Optional device metadata captured for the event.
 * @property timestamp Event timestamp in milliseconds since the Unix epoch.
 * @property sessionId Session identifier associated with the event.
 */
export interface ConvexAnalyticsEvent {
  event: string;
  type: string;
  properties?: Record<string, unknown>;
  url: string;
  referrer?: string;
  userId?: string;
  device?: {
    browser: string;
    os: string;
    device: "desktop" | "tablet" | "mobile";
    screenWidth: number;
    screenHeight: number;
    language: string;
  };
  timestamp: number;
  sessionId: string;
}

/**
 * Normalizes packaged Convex event rows into the shared analytics-event model.
 *
 * @param events Convex analytics rows returned by packaged queries.
 * @returns Shared analytics events ready for UI and helper consumption.
 */
export function normalizeAnalyticsEvents(
  events: ConvexAnalyticsEvent[],
): AnalyticsEvent[] {
  return events.map((event) => ({
    id: `${event.sessionId}:${event.timestamp}:${event.event}`,
    type: normalizeEventType(event.type),
    name: event.event,
    properties: event.properties,
    url: event.url,
    referrer: event.referrer ?? "",
    sessionId: event.sessionId,
    userId: event.userId,
    device: event.device ?? {
      browser: "unknown",
      os: "unknown",
      device: "desktop",
      screenWidth: 0,
      screenHeight: 0,
      language: "en",
    },
    timestamp: event.timestamp,
  }));
}

/**
 * Builds the shared dashboard-data summary from raw page views and events.
 *
 * @param options Source data used to derive dashboard metrics and breakdowns.
 * @returns Aggregated dashboard data used by the published UI variants.
 */
export function buildAnalyticsDashboardData(options: {
  pageViews?: Array<{
    url: string;
    title?: string;
    referrer?: string;
    duration?: number;
    sessionId: string;
    userId?: string;
    timestamp: number;
  }>;
  events?: ConvexAnalyticsEvent[];
  topPages?: AnalyticsDashboardData["topPages"];
  deviceBreakdown?: AnalyticsDashboardData["deviceBreakdown"];
}): AnalyticsDashboardData {
  const pageViews = options.pageViews ?? [];
  const events = options.events ?? [];
  const derivedDeviceBreakdown = events.reduce<
    AnalyticsDashboardData["deviceBreakdown"]
  >(
    (breakdown, event) => {
      switch (event.device?.device) {
        case "desktop":
          breakdown.desktop += 1;
          break;
        case "tablet":
          breakdown.tablet += 1;
          break;
        case "mobile":
          breakdown.mobile += 1;
          break;
        default:
          break;
      }
      return breakdown;
    },
    { desktop: 0, tablet: 0, mobile: 0 },
  );

  const topPages =
    options.topPages ??
    (() => {
      const summaries = new Map<
        string,
        {
          views: number;
          visitorIds: Set<string>;
          durationTotal: number;
          durationCount: number;
        }
      >();
      for (const pageView of pageViews) {
        const summary = summaries.get(pageView.url) ?? {
          views: 0,
          visitorIds: new Set<string>(),
          durationTotal: 0,
          durationCount: 0,
        };
        summary.views += 1;
        summary.visitorIds.add(pageView.userId ?? pageView.sessionId);
        if (typeof pageView.duration === "number") {
          summary.durationTotal += pageView.duration;
          summary.durationCount += 1;
        }
        summaries.set(pageView.url, summary);
      }

      return [...summaries.entries()]
        .sort((left, right) => right[1].views - left[1].views)
        .map<TopPageSummary>(([url, summary]) => ({
          url,
          views: summary.views,
          uniqueVisitors: summary.visitorIds.size,
          averageTimeMs:
            summary.durationCount > 0
              ? Math.round(summary.durationTotal / summary.durationCount)
              : 0,
        }));
    })();

  const referrerCounts = new Map<string, number>();
  for (const pageView of pageViews) {
    if (!pageView.referrer) continue;
    referrerCounts.set(
      pageView.referrer,
      (referrerCounts.get(pageView.referrer) ?? 0) + 1,
    );
  }

  const browserCounts = new Map<string, number>();
  const visitorIds = new Set<string>();

  for (const pageView of pageViews) {
    visitorIds.add(pageView.userId ?? pageView.sessionId);
  }

  for (const event of events) {
    visitorIds.add(event.userId ?? event.sessionId);
    if (event.device?.browser) {
      browserCounts.set(
        event.device.browser,
        (browserCounts.get(event.device.browser) ?? 0) + 1,
      );
    }
  }

  return {
    totalPageViews: pageViews.length,
    uniqueVisitors: visitorIds.size,
    topPages,
    topReferrers: [...referrerCounts.entries()]
      .sort((left, right) => right[1] - left[1])
      .map(([referrer, visits]) => ({ referrer, visits })),
    deviceBreakdown: options.deviceBreakdown ?? derivedDeviceBreakdown,
    browserBreakdown: Object.fromEntries(browserCounts.entries()),
  };
}

/**
 * Builds a realtime dashboard snapshot from recent analytics activity.
 *
 * @param options Source records and window controls used to derive live metrics.
 * @returns Realtime stats populated with sessions, rate, conversions, and devices.
 */
export function buildRealtimeStats(
  options: {
    events?: ConvexAnalyticsEvent[];
    pageViews?: Array<{
      url: string;
      sessionId: string;
      userId?: string;
      timestamp: number;
    }>;
    now?: number;
    windowMs?: number;
    conversionEventNames?: string[];
    topPageLimit?: number;
  } = {},
): RealtimeStats {
  const now = options.now ?? Date.now();
  const windowMs = Math.max(1, options.windowMs ?? 60_000);
  const activeSince = now - windowMs;
  const activeEvents = (options.events ?? []).filter(
    (event) => event.timestamp >= activeSince,
  );
  const activePageViews = (options.pageViews ?? []).filter(
    (pageView) => pageView.timestamp >= activeSince,
  );
  const visitorIds = new Set<string>();
  const sessionIds = new Set<string>();
  const pageVisitors = new Map<string, Set<string>>();
  const conversionEventNames = new Set(options.conversionEventNames ?? []);
  const deviceBreakdown: AnalyticsDashboardData["deviceBreakdown"] = {
    desktop: 0,
    tablet: 0,
    mobile: 0,
  };

  const addIdentity = (record: {
    sessionId: string;
    userId?: string;
  }): string => {
    const visitorId = record.userId ?? record.sessionId;
    visitorIds.add(visitorId);
    sessionIds.add(record.sessionId);
    return visitorId;
  };

  for (const pageView of activePageViews) {
    const visitorId = addIdentity(pageView);
    const visitors = pageVisitors.get(pageView.url) ?? new Set<string>();
    visitors.add(visitorId);
    pageVisitors.set(pageView.url, visitors);
  }

  for (const event of activeEvents) {
    const visitorId = addIdentity(event);
    const visitors = pageVisitors.get(event.url) ?? new Set<string>();
    visitors.add(visitorId);
    pageVisitors.set(event.url, visitors);

    switch (event.device?.device) {
      case "desktop":
        deviceBreakdown.desktop += 1;
        break;
      case "tablet":
        deviceBreakdown.tablet += 1;
        break;
      case "mobile":
        deviceBreakdown.mobile += 1;
        break;
      default:
        break;
    }
  }

  const conversions = activeEvents.filter((event) => {
    if (conversionEventNames.has(event.event)) return true;
    return event.event.toLowerCase().includes("conversion");
  }).length;
  const eventWindowSeconds = windowMs / 1000;

  return {
    activeVisitors: visitorIds.size,
    currentPageViews: activePageViews.length,
    activeSessions: sessionIds.size,
    eventsPerSecond: Number(
      (activeEvents.length / eventWindowSeconds).toFixed(2),
    ),
    conversions,
    conversionRate:
      activeEvents.length > 0 ? (conversions / activeEvents.length) * 100 : 0,
    deviceBreakdown,
    topActivePages: [...pageVisitors.entries()]
      .sort((left, right) => right[1].size - left[1].size)
      .slice(0, options.topPageLimit ?? 5)
      .map(([url, visitors]) => ({ url, visitors: visitors.size })),
    lastUpdated: now,
  };
}
